import { useContext, useEffect, useState, memo } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAlert from '../../../Alerts/useAlert';
import usePrivileges from '../../../Auth/usePrivileges';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import DataTable from '../../../DataTable/datatable.component';
import NoFilter from '../../../DataTable/datatable.nofilter';
import CommentDialog from '../../../Dialogs/CommentDialog';
import EditableQualityColumn from './QualtiesSection/EditableQualityColumn';
import LBMLabColumn from './QualtiesSection/LBMLabColumn';
import PPKColumn from './QualtiesSection/PPKColumn';
import QualityColumn from './QualtiesSection/QualityColumn';
import QualityGraphGeneratorCell from './QualtiesSection/QualityGraphGeneratorCell';
import UsedQualityValueSelectCell from './QualtiesSection/UsedQualityValueSelectCell';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { QualityColumnType } from '../../../Types/LotDetailQuality';
import { LRLColumn } from './QualtiesSection/LRLColumn';
import { URLColumn } from './QualtiesSection/URLColumn';
import { SampleUsagesColumn } from './QualtiesSection/SampleUsagesColumn';
import { QualityValueConfidenceColumn } from './QualtiesSection/QualityValueConfidenceColumn';
import CentredSpinner from '../../../Components/CentredSpinner';

interface QualitiesSectionProps {
    customQualityColumns?: QualityColumnType[];
    showGraph?: boolean;
}

const QualitiesSection = memo(function QualitiesSection({ customQualityColumns = [], showGraph = true }: QualitiesSectionProps) {
    const { t } = useTranslation();
    const {
        lotDetail: {
            summary: {
                lotNumber,
                businessUnitCode,
                isLocked,
                currentStatus
            },
            qualities: initialQualities
        },
        getLotDetail,
        getLotQualities,
        loadingQualities
    } = useContext(LotDetailContext);

    const { fetchDataAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const { canReview } = usePrivileges(businessUnitCode);
    const [hasChanges, setHasChanges] = useState(false);
    const [qualities, setQualities] = useState(JSON.parse(JSON.stringify(initialQualities ?? [])));
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [modifiedRows, setModifiedRows] = useState<number[]>([]);

    const defaultColumns: QualityColumnType[] = [
        "Name",
        "Primary",
        "QualityValueConfidence",
        "Secondary",
        "Tertiary",
        "Manual",
        "L3Lab",
        "LBMLab",
        "Min",
        "Target",
        "Max",
        "Units",
        "PPK",
        "SelectUsedValue",
        "displayOrder",
    ];

    useEffect(() => {
        setQualities(JSON.parse(JSON.stringify(initialQualities ?? [])));
    }, [initialQualities]);

    useEffect(() => {
        getLotQualities();
    }, [getLotQualities]);

    useEffect(() => {
        let modifiedRows = [];
        if (initialQualities?.length > 0 && qualities?.length > 0) {
            for (let qualityIndex = 0; qualityIndex < qualities.length; qualityIndex++) {
                let originalQuality = initialQualities[qualityIndex];
                let newQuality = qualities[qualityIndex];

                if (originalQuality && newQuality) {
                    if (originalQuality.qualityValues.length !== newQuality.qualityValues.length) {
                        modifiedRows.push(qualityIndex);
                    }
                    for (let qualityValueIndex = 0; qualityValueIndex < newQuality.qualityValues.length; qualityValueIndex++) {
                        let orginalQualityValue = originalQuality.qualityValues[qualityValueIndex];
                        let newQualityValue = newQuality.qualityValues[qualityValueIndex];

                        if (newQualityValue.resultValue !== orginalQualityValue?.resultValue) {
                            modifiedRows.push(qualityIndex);
                        }
                        if (newQualityValue.status !== orginalQualityValue?.status) {
                            modifiedRows.push(qualityIndex);
                        }
                    }
                }
            }
        }

        setModifiedRows(modifiedRows);
        if (modifiedRows.length === 0) {
            setHasChanges(false);
        } else {
            setHasChanges(true);
        }
    }, [qualities, initialQualities]);

    const handleSaveWithComment = (comment: string) => {
        qualities[0].comment = comment;

        fetchDataAuthenticated(`/api/v1/lot/${lotNumber}/qualities`, 'PUT', JSON.stringify(qualities))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", "Quality values updated");
                    getLotDetail();
                } else {
                    let errorMessage = await response.json();
                    setShowCommentDialog(false);
                    throw new Error('Failed to update quality values. ' + errorMessage);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            });
    };

    const handleUndo = () => {
        setQualities(JSON.parse(JSON.stringify(initialQualities)));
        setHasChanges(false);
    };

    let canEdit = canReview && !isLocked && currentStatus !== "Graded";

    // The set of columns to actually display (may be customized)
    let qualityColumns = customQualityColumns.length > 0 ? customQualityColumns : defaultColumns;

    const qualityColumnTypeToColumn = (columnType: QualityColumnType): any => {
        switch (columnType) {
            case "Name":
                return {
                    Header: t('LotDetails.Qualities.Name'),
                    accessor: 'name',
                    filter: 'includesString'
                };
            case "Primary":
                return QualityColumn({ header: t('LotDetails.Qualities.Primary'), qualityType: 'ASADDED', qualityColumns: defaultColumns });
            case "Secondary":
                return QualityColumn({ header: t('LotDetails.Qualities.Secondary'), qualityType: 'SECONDARYASADDED' });
            case "Tertiary":
                return QualityColumn({ header: t('LotDetails.Qualities.Tertiary'), qualityType: 'TERTIARYASADDED' });
            case "Manual":
                return EditableQualityColumn({ header: t('LotDetails.Qualities.Manual'), qualityType: 'ALTERNATE', qualities, setQualities, canEdit: canEdit });
            case "L3Lab":
                return QualityColumn({ header: t('LotDetails.Qualities.L3Lab'), qualityType: 'LABCALC' });
            case "LBMLab":
                return LBMLabColumn({ header: t('LotDetails.Qualities.LBMLab'), qualityType: 'LBMLABCALC', qualityColumns: qualityColumns });
            case "LBMSecondaryLab":
                return LBMLabColumn({ header: t('LotDetails.Qualities.LBMSecondaryLab'), qualityType: 'SECONDARYLBMLABCALC', qualityColumns: qualityColumns });
            case "Min":
                return {
                    Header: t('LotDetails.Qualities.Min'),
                    accessor: 'min',
                    Filter: NoFilter
                };
            case "Max":
                return {
                    Header: t('LotDetails.Qualities.Max'),
                    accessor: 'max',
                    Filter: NoFilter
                };
            case "Target":
                return {
                    Header: t('LotDetails.Qualities.Target'),
                    accessor: 'target',
                    Filter: NoFilter
                };
            case "LBMLabMin":
                return QualityColumn({ header: t('LotDetails.Qualities.LBMLabMin'), qualityType: 'LBMLABMIN' });
            case "LBMLabMax":
                return QualityColumn({ header: t('LotDetails.Qualities.LBMLabMax'), qualityType: 'LBMLABMAX' });
            case "LRL":
                return LRLColumn();
            case "URL":
                return URLColumn();
            case "SalesMin":
                return {
                    Header: t('LotDetails.Qualities.SalesMin'),
                    accessor: 'salesMin',
                    Filter: NoFilter
                };
            case "SalesMax":
                return {
                    Header: t('LotDetails.Qualities.SalesMax'),
                    accessor: 'salesMax',
                    Filter: NoFilter
                };
            case "PPK":
                return PPKColumn();
            case "QualityValueConfidence":
                return QualityValueConfidenceColumn();
            case "Units":
                return {
                    Header: t('LotDetails.Qualities.Units'),
                    accessor: 'units',
                    Filter: NoFilter
                };
            case "SelectUsedValue":
                return {
                    Header: t('LotDetails.Qualities.SelectUsedValue'),
                    accessor: 'selectUsedValue',
                    Filter: NoFilter,
                    Cell: (props: any) => <UsedQualityValueSelectCell row={props.row} qualities={qualities} setQualities={setQualities} canEdit={canEdit} />
                };
            case "SampleUsages":
                return SampleUsagesColumn({ header: t('LotDetails.Qualities.Usage'), qualityType: 'LBMLABCALC' });
            case "displayOrder":
                return {
                    accessor: 'displayOrder'
                };
        }
    };

    var tableColumns = qualityColumns.map(col => qualityColumnTypeToColumn(col));

    if (showGraph) {
        tableColumns.push({
            accessor: 'showGraphButton',
            Filter: NoFilter,
            Cell: (props: any) => <QualityGraphGeneratorCell row={props.row} lotNumber={lotNumber} />
        });
    }

    const getRowProps = (row: any) => {
        if (modifiedRows.includes(row.index))
            return { className: "bg-info bg-opacity-75" };

        if (row.original.isOffspec)
            return { className: "offspec" };

        return {};
    };

    const getCellProps = (cell: any) => {
        let className = "align-middle";
        if (cell.column.id === "name") {
            className += " text-nowrap";
        }
        return { className: className };
    };

    const initialState = {
        hiddenColumns: ['displayOrder'],
        sortBy: [
            {
                id: 'displayOrder',
                desc: false
            }
        ],
        pageSize: 100
    };

    if (loadingQualities) {
        return <CentredSpinner />;
    }

    return qualities && qualities.length > 0
        ? <>
            <DataTable tableColumns={tableColumns} tableData={qualities} initialState={initialState} getRowProps={getRowProps} getCellProps={getCellProps} showPaging={false} />
            <div className="d-flex">
                <Button className="ms-auto" variant="secondary" hidden={!hasChanges} onClick={handleUndo}>Undo</Button>
                <Button className="ms-2" hidden={!hasChanges} onClick={() => setShowCommentDialog(true)}>Save</Button>
            </div>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={handleSaveWithComment} />
        </>
        : <span>{t('CommonResponses.NoData')}</span>;
});

export default QualitiesSection;